import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity';
import { createReducer, on, Action } from '@ngrx/store';

import * as NotificationsActions from './notifications.actions';
import { Notification } from './notifications.models';
import * as dayjs from 'dayjs';

export const NOTIFICATIONS_FEATURE_KEY = 'notifications';

export interface State extends EntityState<Notification> {
  selectedId?: number; // which Notifications record has been selected
  loaded: boolean; // has the Notifications list been loaded
  error?: string | null; // last known error (if any)
  unread: boolean;
}

export interface NotificationsPartialState {
  readonly [NOTIFICATIONS_FEATURE_KEY]: State;
}

export const notificationsAdapter: EntityAdapter<Notification> = createEntityAdapter<Notification>({
  selectId: (notification) => notification.id,
  sortComparer: (a,b) => dayjs(b.date).diff(dayjs(a.date))});

export const initialState: State = notificationsAdapter.getInitialState({
  // set initial required properties
  loaded: false,
  unread: false
});

const notificationsReducer = createReducer(
  initialState,
  on(NotificationsActions.add, (state, {notification}) => notificationsAdapter.addOne(notification, state)),

  on(NotificationsActions.deleteOneSuccess, (state, {id}) => notificationsAdapter.removeOne(id, state)),
  on(NotificationsActions.deleteOneFailure,  (state, {error}) => ({
    ...state,
    error
  })),

  on(NotificationsActions.load, (state) => ({ ...state, loaded: false, error: null })),
  on(NotificationsActions.loadNotificationsSuccess, (state, { notifications }) =>
    notificationsAdapter.setAll(notifications, { ...state, loaded: true })
  ),
  on(NotificationsActions.loadNotificationsFailure, (state, { error }) => ({ ...state, error })),

  on(NotificationsActions.markAsRead, (state, {notification}) => notificationsAdapter.updateOne(notification, state)),
  on(NotificationsActions.markAsReadUndo, (state, {id}) => notificationsAdapter.updateOne({id, changes: {isRead: false}}, state)),

  on(NotificationsActions.markAsUnread, (state, {notification}) => notificationsAdapter.updateOne(notification, state)),
  on(NotificationsActions.markAsUnreadUndo, (state, {id}) => notificationsAdapter.updateOne({id, changes: {isRead: false}}, state)),

  on(NotificationsActions.toggleUnread, (state, {unread}) => ({
    ...state,
    unread
  }))
);

export function reducer(state: State | undefined, action: Action) {
  return notificationsReducer(state, action);
}
